import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export const usePortfolioImages = (userId?: string) => {
  return useQuery({
    queryKey: ["portfolio-images", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("portfolio_images")
        .select("*")
        .eq("user_id", userId!)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data;
    },
  });
};

export const useAddPortfolioImage = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, caption }: { file: File; caption?: string }) => {
      if (!user) throw new Error("Non connecté");

      const ext = file.name.split(".").pop();
      const path = `${user.id}/${crypto.randomUUID()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("portfolio")
        .upload(path, file);
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("portfolio").getPublicUrl(path);

      const { error } = await supabase
        .from("portfolio_images")
        .insert({
          user_id: user.id,
          image_url: urlData.publicUrl,
          caption: caption || null,
        });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-images"] });
    },
  });
};

export const useDeletePortfolioImage = () => {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string) => {
      if (!user) throw new Error("Non connecté");
      const { error } = await supabase
        .from("portfolio_images")
        .delete()
        .eq("id", imageId)
        .eq("user_id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portfolio-images"] });
    },
  });
};
